import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getMarianaTekReservations, getMarianaTekClassSession, getMarianaTekCreditTransaction, getMarianaTekMembershipTransaction } from '../services/api'
import TabContainer from './TabContainer'

const ReservationsContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const ReservationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const ReservationCard = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 8px ${theme.color.shadow.medium};
  }
`

const ClassTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1rem;
`

const ReservationInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`

const InfoLabel = styled.span`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`

const InfoValue = styled.span`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  font-weight: 500;
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'check in':
        return '#28a745'
      case 'reserved':
        return '#007bff'
      case 'cancelled':
        return '#dc3545'
      case 'waitlist':
        return '#ffc107'
      default:
        return '#6c757d'
    }
  }};
  color: white;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

interface ReservationsProps {
  userData: any
  reservationsData: any
}

interface Reservation {
  id: string
  status: string
  creation_date: string
  check_in_date: string | null
  cancel_date: string | null
  class_session_id: string
  class_type_display?: string
  start_datetime?: string
  instructor_names?: string[]
  location_display?: string
  duration?: number
  credit_name?: string
}

const Reservations: React.FC<ReservationsProps> = ({ userData, reservationsData }) => {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const loadingRef = React.useRef(false)
  const hasLoadedRef = React.useRef(false)

  useEffect(() => {
    // Prevent concurrent loads
    if (loadingRef.current) {
      console.log('Already loading reservations, skipping duplicate request')
      return
    }

    // Prevent reloading if we've already loaded data successfully
    if (hasLoadedRef.current && reservations.length > 0) {
      console.log('Reservations already loaded, skipping reload')
      return
    }

    // If reservations data is already provided, use it
    if (reservationsData) {
      loadReservationsFromData(reservationsData)
    } else {
      // Fallback: fetch if not provided
      const marianaTekId = userData?.user?.external_id
      if (marianaTekId) {
        loadReservations(marianaTekId)
      }
    }
  }, [userData, reservationsData])

  const loadReservationsFromData = async (reservationsData: any) => {
    loadingRef.current = true
    setIsLoading(true)
    setError('')

    try {
      console.log('Using pre-fetched reservations data:', reservationsData)
      const reservationsList = reservationsData.data || []

      await processReservationsList(reservationsList)
      hasLoadedRef.current = true
    } catch (err) {
      console.error('Failed to process reservations:', err)
      setError(err instanceof Error ? err.message : 'Failed to process reservations')
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }

  const loadReservations = async (marianaTekId: string) => {
    loadingRef.current = true
    setIsLoading(true)
    setError('')

    try {
      const reservationsData = await getMarianaTekReservations(marianaTekId, 4)
      console.log('Reservations API response:', reservationsData)

      const reservationsList = reservationsData.data || []

      await processReservationsList(reservationsList)
      hasLoadedRef.current = true
    } catch (err) {
      console.error('Failed to load reservations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load reservations')
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }

  const shouldHideReservationByStatus = (status: string) => {
    const hideStatuses = [
      'grace cancel',
      'removed',
      'standard cancel',
      'penalty no show'
    ]
    const statusLower = status.toLowerCase()
    return hideStatuses.some(hideStatus => statusLower.includes(hideStatus))
  }

  const processReservationsList = async (reservationsList: any[]) => {
    // Filter out unwanted statuses before processing
    const filteredList = reservationsList.filter((reservation: any) => {
      return !shouldHideReservationByStatus(reservation.attributes?.status || '')
    })

    console.log(`Filtered ${reservationsList.length} reservations down to ${filteredList.length} (removed unwanted statuses)`)

    // Process reservations in batches to avoid overwhelming the API
    const BATCH_SIZE = 10
    const reservationsWithDetails: any[] = []

    for (let i = 0; i < filteredList.length; i += BATCH_SIZE) {
      const batch = filteredList.slice(i, i + BATCH_SIZE)

      const batchResults = await Promise.all(
        batch.map(async (reservation: any) => {
          try {
            const classSessionId = reservation.relationships?.class_session?.data?.id
            const creditTransactionId = reservation.relationships?.credit_transactions?.data?.[0]?.id
            const membershipTransactionId = reservation.relationships?.membership_transactions?.data?.[0]?.id

            // Debug logging
            console.log('Reservation debug:', {
              id: reservation.id,
              creditTransactionId: creditTransactionId,
              membershipTransactionId: membershipTransactionId,
              hasClassSession: !!classSessionId,
              allRelationships: reservation.relationships
            })

            if (!classSessionId) {
              return {
                id: reservation.id,
                status: reservation.attributes.status,
                creation_date: reservation.attributes.creation_date,
                check_in_date: reservation.attributes.check_in_date,
                cancel_date: reservation.attributes.cancel_date,
                class_session_id: '',
                credit_name: undefined
              }
            }

            // Fetch class session and payment transaction with timeout
            const classSessionPromise = getMarianaTekClassSession(classSessionId)

            let paymentName = null

            // Try to fetch credit transaction first
            if (creditTransactionId) {
              try {
                const timeoutPromise = new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Credit transaction timeout')), 5000)
                )
                const creditTransactionData = await Promise.race([
                  getMarianaTekCreditTransaction(creditTransactionId),
                  timeoutPromise
                ]) as any
                paymentName = creditTransactionData?.data?.attributes?.credit_name
              } catch (err) {
                console.warn(`Failed to fetch credit transaction ${creditTransactionId}:`, err)
              }
            }

            // If no credit transaction, try membership transaction
            if (!paymentName && membershipTransactionId) {
              try {
                const timeoutPromise = new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Membership transaction timeout')), 5000)
                )
                const membershipTransactionData = await Promise.race([
                  getMarianaTekMembershipTransaction(membershipTransactionId),
                  timeoutPromise
                ]) as any
                paymentName = membershipTransactionData?.data?.attributes?.membership_name
              } catch (err) {
                console.warn(`Failed to fetch membership transaction ${membershipTransactionId}:`, err)
              }
            }

            const classSessionData = await classSessionPromise

            return {
              id: reservation.id,
              status: reservation.attributes.status,
              creation_date: reservation.attributes.creation_date,
              check_in_date: reservation.attributes.check_in_date,
              cancel_date: reservation.attributes.cancel_date,
              class_session_id: classSessionId,
              class_type_display: classSessionData.data?.attributes?.class_type_display,
              start_datetime: classSessionData.data?.attributes?.start_datetime,
              instructor_names: classSessionData.data?.attributes?.instructor_names,
              location_display: classSessionData.data?.attributes?.location_display,
              duration: classSessionData.data?.attributes?.duration,
              credit_name: paymentName
            }
          } catch (err) {
            console.error('Failed to load reservation details:', err)
            return {
              id: reservation.id,
              status: reservation.attributes.status,
              creation_date: reservation.attributes.creation_date,
              check_in_date: reservation.attributes.check_in_date,
              cancel_date: reservation.attributes.cancel_date,
              class_session_id: reservation.relationships?.class_session?.data?.id || '',
              credit_name: undefined
            }
          }
        })
      )

      reservationsWithDetails.push(...batchResults)

      // Update state after each batch so user sees progressive loading
      setReservations([...reservationsWithDetails])
    }
  }

  const formatDateTime = (dateTimeString: string | null | undefined) => {
    if (!dateTimeString) return 'N/A'
    const date = new Date(dateTimeString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getCurrentReservations = () => {
    const now = new Date()
    return reservations.filter(reservation => {
      // Statuses are already filtered out during processReservationsList

      // Cancelled reservations go to Past
      if (reservation.status.toLowerCase() === 'cancelled') return false

      // Checked-in reservations go to Past
      if (reservation.status.toLowerCase() === 'check in') return false

      // Reserved and Waitlist reservations show in Current
      if (reservation.status.toLowerCase() === 'reserved' || reservation.status.toLowerCase() === 'waitlist') return true

      // If no date, show in current
      if (!reservation.start_datetime) return true

      // Show in current only if start time is in the future
      const startDate = new Date(reservation.start_datetime)
      return startDate >= now
    })
  }

  const getPastReservations = () => {
    const now = new Date()
    return reservations.filter(reservation => {
      // Statuses are already filtered out during processReservationsList

      // Cancelled reservations always go to Past
      if (reservation.status.toLowerCase() === 'cancelled') return true

      // Checked-in reservations always go to Past
      if (reservation.status.toLowerCase() === 'check in') return true

      // If no date, don't show in past
      if (!reservation.start_datetime) return false

      // Show in past if start time has passed
      const startDate = new Date(reservation.start_datetime)
      return startDate < now
    })
  }

  const renderReservations = (reservationsList: Reservation[]) => {
    if (isLoading) {
      return <LoadingMessage>Loading reservations...</LoadingMessage>
    }

    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>
    }

    if (reservationsList.length === 0) {
      return <NoDataMessage>No reservations found</NoDataMessage>
    }

    return (
      <ReservationsList>
        {reservationsList.map(reservation => (
          <ReservationCard key={reservation.id}>
            <ClassTitle>
              {reservation.class_type_display || 'Class Details Not Available'}
            </ClassTitle>
            <ReservationInfo>
              <InfoItem>
                <InfoLabel>Date & Time</InfoLabel>
                <InfoValue>{formatDateTime(reservation.start_datetime)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Instructor</InfoLabel>
                <InfoValue>
                  {reservation.instructor_names?.join(', ') || 'N/A'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>{reservation.location_display || 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Duration</InfoLabel>
                <InfoValue>{reservation.duration ? `${reservation.duration} min` : 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Payment Method</InfoLabel>
                <InfoValue>{reservation.credit_name || 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <StatusBadge status={reservation.status}>
                    {reservation.status}
                  </StatusBadge>
                </InfoValue>
              </InfoItem>
              {reservation.check_in_date && (
                <InfoItem>
                  <InfoLabel>Check-in Time</InfoLabel>
                  <InfoValue>{formatDateTime(reservation.check_in_date)}</InfoValue>
                </InfoItem>
              )}
            </ReservationInfo>
          </ReservationCard>
        ))}
      </ReservationsList>
    )
  }

  if (!userData || !userData.user) {
    return null
  }

  const marianaTekId = userData?.user?.external_id

  if (!marianaTekId) {
    return (
      <ReservationsContainer>
        <Title>Reservations</Title>
        <NoDataMessage>No Mariana Tek ID found for this user</NoDataMessage>
      </ReservationsContainer>
    )
  }

  return (
    <ReservationsContainer>
      <Title>Reservations</Title>
      <TabContainer
        tabs={[
          {
            id: 'current',
            label: 'Current',
            content: renderReservations(getCurrentReservations())
          },
          {
            id: 'past',
            label: 'Past',
            content: renderReservations(getPastReservations())
          }
        ]}
        defaultTab="current"
      />
    </ReservationsContainer>
  )
}

export default Reservations
